<template>
    <v-dialog :model-value="modelValue" @update:model-value="close" :max-width="isPhone ? undefined : 800"
        :fullscreen="isPhone">
        <template v-if="$slots.activator" v-slot:activator="slot_props">
            <slot name="activator" v-bind="slot_props"></slot>
        </template>
        <v-card :loading="loading ? 'accent' : false">
            <v-card-title class="text-h5">
                {{ t("change_station") }}
            </v-card-title>
            <v-card-subtitle>
                {{ t("change_station_help_text") }}
            </v-card-subtitle>
            <v-card-text v-if="note" class="pb-0">
                <v-alert type="warning" variant="tonal" density="compact" :icon="false">
                    {{ note }}
                </v-alert>
            </v-card-text>
            <v-card-text v-if="production_lines.length == 0 && loading">
                <v-skeleton-loader type="list-item-avatar-three-line@2">
                </v-skeleton-loader>
            </v-card-text>
            <v-card-text v-else>
                <h3 v-if="show_no_station">
                    {{ t("no_production_line") }}
                </h3>
                <div v-if="show_no_station" class="d-flex ma-3">
                    <v-card border class="ma-1" :color="selected_station == null ? 'accent' : ''" width="220"
                        height="120" @click="clean_station">
                        <v-card-title>
                            <v-icon start> mdi-close </v-icon>
                            {{ t("no_station") }}
                        </v-card-title>
                    </v-card>
                </div>
                <div v-for="pl in production_lines" :key="pl.uuid">
                    <h3>{{ pl.name }}</h3>
                    <div :class="isPhone ? 'my-3' : 'ma-3'" :style="isPhone ? '' : 'overflow-x: auto; white-space: nowrap;'">
                        <v-card v-for="sta in get_pl_stations(pl)" :key="sta.uuid" @click="change_station(sta, pl)" border
                            :class="isPhone ? 'my-2' : 'ma-1 d-inline-block'"
                            :color="selected_station == sta.uuid && selected_line == pl.uuid ? 'accent' : ''"
                            :width="isPhone ? undefined : 180" :height="isPhone ? undefined : 120"
                            :min-height="isPhone ? 72 : undefined">
                            <v-card-title class="text-truncate">
                                <v-icon start> mdi-toolbox </v-icon>
                                <v-tooltip location="bottom">
                                    <template v-slot:activator="{ props }">
                                        <span v-bind="props">
                                            {{ sta.name }}
                                        </span>
                                    </template>
                                    {{ sta.name }}
                                </v-tooltip>
                            </v-card-title>
                            <v-card-text>
                                <IconStation :station="sta" />
                            </v-card-text>
                        </v-card>
                    </div>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="secondary" variant="text" @click="close(false)">
                    {{ t("close") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { useFactoriesStore } from "@/stores/factories";
import { useSessionStore } from "@/stores/session";
import { useTranslationsStore } from "@/stores/translations";
import { useIsPhone } from "@/composables/display";
import { nameSort } from "/src/utils/basics.js";
import IconStation from "/src/components/renderers/IconStation.vue"

// The Change Station dialog. Opened from the account menu with every line the
// account may see, and from the viewer's refusal banner with `production_line`
// set, which shows that line only.
export default {
    components: {
        IconStation,
    },
    props: {
        modelValue: { type: Boolean, default: false },
        // A line uuid: only that line, and no "No station" card.
        production_line: { type: String, default: null },
        // A line of text under the subtitle, for what the caller needs said.
        note: { type: String, default: "" },
    },
    emits: ["update:modelValue", "selected"],
    setup() {
        const factories_store = useFactoriesStore()
        const session_store = useSessionStore()
        const translations_store = useTranslationsStore()
        const t = translations_store.translate
        return {
            factories_store,
            session_store,
            t,
            ...useIsPhone(),
        }
    },
    data: () => ({
        loading: false,
    }),
    computed: {
        production_lines() {
            let pls = this.factories_store.production_lines;
            if (this.production_line) {
                pls = pls.filter((pl) => pl.uuid == this.production_line)
            } else {
                // Filter visibles
                pls = pls.filter(
                    (pl) => pl.uuid == this.my_line || this.i_can_see_other_lines
                );
            }
            return pls.sort((a, b) => nameSort(a.name, b.name))
        },
        show_no_station() {
            return this.i_can_see_other_lines && !this.production_line
        },
        my_line() {
            return this.session_store.line_uuid
        },
        selected_station() {
            return this.session_store.user_object.auth
                .selected_production_station;
        },
        selected_line() {
            return this.session_store.user_object.auth.selected_production_line
        },
        i_can_see_other_lines() {
            return (
                this.session_store.user_object?.auth
                    ?.company_admin ||
                this.session_store.user_object?.auth
                    ?.company_manager ||
                this.session_store.user_object?.auth
                    ?.can_select_production_line
            );
        },
    },
    methods: {
        close(value = false) {
            this.$emit("update:modelValue", value)
        },
        get_pl_stations(pl) {
            return this.factories_store.getPLStations(pl)
        },
        change_station(sta, pl) {
            this.loading = true;
            this.session_store.selectStation(sta.uuid, pl.uuid)
                .then(() => {
                    this.$emit("selected", { station: sta.uuid, production_line: pl.uuid })
                    this.close(false)
                })
                .catch(() => { })
                .finally(() => (this.loading = false));
        },
        clean_station() {
            this.loading = true;
            this.session_store.selectStation("")
                .then(() => this.close(false))
                .catch(() => { })
                .finally(() => (this.loading = false));
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(d) {
                if (!d) return
                this.loading = true;
                const loads = [this.factories_store.loadProductionLines()]
                if (this.factories_store.stations.length == 0) loads.push(this.factories_store.loadStations())
                Promise.all(loads).finally(() => (this.loading = false));
            },
        },
    },
};
</script>
